import Cookies from 'js-cookie';

import { applyNativeConsent, hasNativeAnalytics } from '../../analytics/nativeFirebase';
import config from '../../config';
import { logError } from '../log';
import { isAndroid, isCordova, isIOS } from '../mobile/deviceDetection';
import hasEffectiveConsent from './effectiveConsent';

const GOOGLE_COOKIE_PREFIX = '_ga';
const FACEBOOK_COOKIES = ['_fbp', '_fbc'];

const updateGtagConsent = (consent) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  window.gtag('consent', 'update', {
    analytics_storage: consent.statistics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
  });
};

// eTLD+1 for the domain-scoped cookie removal. Null for hosts with no dot
// (localhost) or IP literals, where a leading-dot domain cookie is meaningless.
const rootDomainOf = (hostname) => {
  if (!hostname || !hostname.includes('.') || /^[\d.]+$/.test(hostname)) return null;
  return `.${hostname.split('.').slice(-2).join('.')}`;
};

const deleteCookiesByName = (names) => {
  const rootDomain = rootDomainOf(window.location.hostname);
  names.forEach((name) => {
    Cookies.remove(name);
    if (rootDomain) Cookies.remove(name, { domain: rootDomain, path: '/' });
  });
};

const deleteGoogleCookies = () => {
  const names = Object.keys(Cookies.get()).filter((n) => n === GOOGLE_COOKIE_PREFIX || n.startsWith(`${GOOGLE_COOKIE_PREFIX}_`));
  deleteCookiesByName(names);
};

// Memoized so concurrent callers (boot bootstrap + a stored decision applied in
// the same tick) share one init instead of racing between the check and the
// assignment. Reset on failure so a later call can retry.
let firebaseSetup = null;

const setupFirebase = async () => {
  if (window.googleAnalytics) return;
  if (!firebaseSetup) {
    firebaseSetup = (async () => {
      const appType = isAndroid() ? 'android' : isIOS() ? 'ios' : 'web';
      const { initializeApp } = await import('firebase/app');
      const { getAnalytics } = await import('firebase/analytics');
      return getAnalytics(initializeApp(config.firebase(appType)));
    })().catch((e) => {
      firebaseSetup = null;
      throw e;
    });
  }
  window.googleAnalytics = await firebaseSetup;
};

const setupFacebookPixel = async () => {
  const ReactPixel = await import('@bettercart/react-facebook-pixel');
  ReactPixel.default.init(config.facebook.pixel.id, {}, config.facebook.pixel.options);
  ReactPixel.default.grantConsent();
};

const revokeFacebookPixel = async () => {
  const ReactPixel = await import('@bettercart/react-facebook-pixel');
  ReactPixel.default.revokeConsent();
};

/**
 * Advanced consent mode: loads the GA4 web SDK on every client boot, even when
 * the banner was never answered. The SSR head defaults all four consent signals
 * to 'denied', so gtag only sends cookieless pings until the user grants
 * storage — without them Google cannot model conversions. No-op in the Cordova
 * app (native SDK, see applyConsent) and outside production. Safe to call
 * repeatedly.
 */
export const bootstrapWebAnalytics = async () => {
  if (typeof window === 'undefined') return;
  if (process.env.REACT_APP_ENV !== 'production') return;
  if (isCordova()) return;
  try {
    await setupFirebase();
  } catch (e) {
    logError(e, 'apply-consent', { message: 'Failed to bootstrap web analytics' });
  }
};

/**
 * Applies a consent decision to all vendors. Safe to call repeatedly.
 * @param {{statistics: boolean, marketing: boolean}} consent
 */
const applyConsent = async (consent) => {
  if (typeof window === 'undefined' || !consent) return;
  try {
    // Consent-mode signals reflect the raw user choice (ATT is a separate,
    // platform-level control); vendor boot below uses the effective gate.
    updateGtagConsent(consent);
    if (process.env.REACT_APP_ENV !== 'production') return;

    // Reuse the single source of truth for effective consent (includes the iOS
    // ATT gate for marketing) instead of re-deriving it here.
    if (isCordova()) {
      // Cordova app: analytics runs through the NATIVE Firebase SDK (app data
      // stream → platform = Android/iOS), never the web JS SDK. Before
      // deviceready the plugin namespace is missing — initCordovaScripts
      // re-applies the stored consent once the plugins are up.
      if (hasNativeAnalytics()) {
        await applyNativeConsent(
          {
            statistics: await hasEffectiveConsent('statistics'),
            marketing: await hasEffectiveConsent('marketing'),
          },
          { resetData: !consent.statistics },
        );
      }
    } else {
      // Web: advanced consent mode — the GA4 SDK stays loaded regardless of the
      // statistics decision. The consent signals updated above are what decides
      // whether a hit is cookieless (gcs=G100) or a full one; unloading GA4 here
      // would silence the cookieless pings Google needs to model conversions.
      // Cookies written during an earlier granted phase are still removed.
      if (!(await hasEffectiveConsent('statistics'))) deleteGoogleCookies();
      await setupFirebase();
    }

    if (await hasEffectiveConsent('marketing')) {
      await setupFacebookPixel();
    } else {
      await revokeFacebookPixel();
      deleteCookiesByName(FACEBOOK_COOKIES);
    }
  } catch (e) {
    logError(e, 'apply-consent', { message: 'Failed to apply consent to vendors' });
  }
};

export default applyConsent;
